import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, DollarSign, ArrowRightLeft } from 'lucide-react';
import { crmDealsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import DealForm from '../../components/crm/DealForm';
import type { CrmDeal, CrmPipelineStage } from '../../types';

function DealCard({ deal, moveTargets, onMoveStage }: { deal: CrmDeal; moveTargets: { id: string; name: string; color: string }[]; onMoveStage: (dealId: string, stageId: string) => void }) {
  // Tap-to-move menu — HTML5 drag-and-drop never fires on touchscreens,
  // so this is the only way to move a deal on mobile.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <div className="relative">
      <Link to={`/crm/deals/${deal.id}`}
      draggable
      onDragStart={e => e.dataTransfer.setData('dealId', deal.id)}
      className="block bg-surface rounded-lg border border-line p-3 hover:bg-row-odd transition-colors cursor-pointer space-y-2"
    >
      <p className="font-medium text-sm text-fg line-clamp-2 pr-6">{deal.title}</p>
      {deal.value && (
        <p className="flex items-center gap-1 text-xs text-ok font-semibold">
          <DollarSign className="w-3 h-3" />{deal.value.toLocaleString()} {deal.currency}
        </p>
      )}
      {deal.contact && (
        <p className="text-xs text-muted">{deal.contact.firstName} {deal.contact.lastName}</p>
      )}
      {deal.organization && (
        <p className="text-xs text-muted">{deal.organization.name}</p>
      )}
      {deal.expectedCloseDate && (
        <p className="text-xs text-muted">Close: {new Date(deal.expectedCloseDate).toLocaleDateString()}</p>
      )}
      {deal.owner && (
        <div className="flex items-center gap-1.5 pt-1 border-t border-line">
          <div className="w-4 h-4 rounded-full bg-primary-muted flex items-center justify-center text-primary text-[10px] font-bold">{deal.owner.name[0]}</div>
          <span className="text-xs text-muted">{deal.owner.name}</span>
        </div>
      )}
    </Link>
      {moveTargets.length > 0 && (
        <div ref={menuRef} className="absolute top-1.5 right-1.5">
          <button
            type="button"
            onClick={() => setMenuOpen(v => !v)}
            className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-row-odd transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Move to stage"
            title="Move to stage"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <div className="scpa-menu right-0 top-full mt-1" role="menu">
              <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-muted">Move to</div>
              {moveTargets.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className="scpa-menu-item"
                  onClick={() => { setMenuOpen(false); onMoveStage(deal.id, s.id); }}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KanbanColumn({ stage, allStages, onDrop, onAddDeal }: { stage: CrmPipelineStage & { deals: CrmDeal[] }; allStages: { id: string; name: string; color: string }[]; onDrop: (dealId: string, stageId: string) => void; onAddDeal: (stageId: string) => void }) {
  const [dragOver, setDragOver] = useState(false);
  const totalValue = stage.deals.reduce((s, d) => s + (d.value ?? 0), 0);
  const moveTargets = allStages.filter(s => s.id !== stage.id);

  return (
    <div
      className={`flex flex-col shrink-0 w-[85vw] max-w-[320px] sm:w-[260px] snap-start rounded-xl border transition-colors ${dragOver ? 'border-primary bg-primary-muted' : 'border-line bg-app'}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const dealId = e.dataTransfer.getData('dealId'); if (dealId) onDrop(dealId, stage.id); }}
    >
      <div className="p-3 border-b border-line">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
            <span className="font-semibold text-sm text-fg">{stage.name}</span>
            <span className="text-xs font-semibold text-muted">{stage.deals.length}</span>
          </div>
          <button onClick={() => onAddDeal(stage.id)} className="p-1 rounded-md text-muted hover:text-primary hover:bg-row-odd transition-colors">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        {totalValue > 0 && (
          <p className="text-xs text-ok mt-1 pl-4">${totalValue.toLocaleString()}</p>
        )}
      </div>
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100dvh-220px)]">
        {stage.deals.map(deal => (
          <DealCard key={deal.id} deal={deal} moveTargets={moveTargets} onMoveStage={onDrop} />
        ))}
        {!stage.deals.length && (
          <div className="text-center py-6 text-xs text-muted">No deals in this stage</div>
        )}
      </div>
    </div>
  );
}

export default function DealBoard() {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [defaultStageId, setDefaultStageId] = useState<string | undefined>();

  const { data: stages, isLoading } = useQuery({
    queryKey: ['crm-deal-board', cid],
    queryFn: () => crmDealsApi.getBoard(),
    enabled: !!cid,
  });

  const moveMutation = useMutation({
    mutationFn: ({ dealId, stageId }: { dealId: string; stageId: string }) => crmDealsApi.moveStage(dealId, stageId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm-deal-board'] }),
  });

  const totalPipelineValue = stages?.flatMap(s => s.deals).reduce((s, d) => s + (d.value ?? 0), 0) ?? 0;
  const totalDeals = stages?.reduce((s, st) => s + st.deals.length, 0) ?? 0;

  return (
    <div className="flex flex-col gap-4 pb-10 h-full">
      <PageHeader
        title="Deals Pipeline"
        intro={isLoading ? 'Drag a deal between stages — or use its move button — to update it.' : `${totalDeals} open deals · $${totalPipelineValue.toLocaleString()} total. Drag a deal between stages — or use its move button — to update it.`}
        actions={
          <>
            <Link to="/crm/deals" className="btn btn-secondary">List View</Link>
            <button onClick={() => { setDefaultStageId(undefined); setShowForm(true); }} className="btn btn-primary flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> New Deal
            </button>
          </>
        }
      />

      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[...Array(5)].map((_, i) => <div key={i} className="shrink-0 w-[85vw] max-w-[320px] sm:w-[260px] h-64 bg-surface rounded-xl animate-pulse" />)}
        </div>
      ) : !stages?.length ? (
        <div className="card flex-1 flex items-center justify-center">
          <div className="text-center text-muted">
            <TrendingUp className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--scpa-text-muted)' }} />
            <p className="text-sm">No pipeline stages configured.</p>
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4 flex-1 snap-x snap-mandatory sm:snap-none">
          {stages.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              allStages={stages}
              onDrop={(dealId, stageId) => moveMutation.mutate({ dealId, stageId })}
              onAddDeal={stageId => { setDefaultStageId(stageId); setShowForm(true); }}
            />
          ))}
        </div>
      )}

      {showForm && (
        <DealForm
          defaultStageId={defaultStageId}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); queryClient.invalidateQueries({ queryKey: ['crm-deal-board'] }); }}
        />
      )}
    </div>
  );
}
